import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RefreshCw, X } from "lucide-react";
import { settlementsApi } from "./api";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field } from "../applications/shared/InfoBlock";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP } from "../dashboard/format";
import { formatDateShort } from "./shared/labels";
import type { ProductorListItem } from "./types";

export function ProducersScreen() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ["settlements", "productores"],
    queryFn: () => settlementsApi.listProductores(),
  });

  const rows = query.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Liquidaciones · Productores</p>
          <h1 className="nx-h1">Productores</h1>
          <p className="text-sm text-fg-muted mt-1">
            Lista de productores con sus clubs, eventos activos y liquidaciones
            pendientes. Click para ver detalle bancario y todos sus eventos.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando productores…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <ApplicationsTable<ProductorListItem>
          rows={rows}
          columns={columns}
          page={1}
          pageSize={rows.length || 1}
          total={rows.length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
          emptyLabel="No hay productores registrados."
        />
      )}

      {selectedId !== null && (
        <ProductorDrawer id={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

const columns: Column<ProductorListItem>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "name",
    header: "Productor",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.nombre}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.email}</div>
      </div>
    ),
  },
  {
    key: "stats",
    header: "Stats",
    cell: (r) => (
      <div className="text-2xs text-fg-muted">
        <div>
          {r.cantClubs} {r.cantClubs === 1 ? "club" : "clubs"} · {r.cantEventosActivos}{" "}
          {r.cantEventosActivos === 1 ? "evento" : "eventos"}
        </div>
        {r.cantPendientes > 0 && (
          <div className="text-warning">
            {r.cantPendientes} {r.cantPendientes === 1 ? "pendiente" : "pendientes"}
          </div>
        )}
      </div>
    ),
  },
  {
    key: "monto",
    header: "Monto pendiente",
    cell: (r) => (
      <span className={`text-xs tabular-nums ${r.montoPendiente > 0 ? "text-warning font-semibold" : "text-fg-subtle"}`}>
        {fmtCLP(r.montoPendiente)}
      </span>
    ),
    align: "right",
    width: "min",
  },
  {
    key: "bank",
    header: "Datos bancarios",
    cell: (r) =>
      r.tienePaymentInfo ? (
        <span className="text-2xs text-success">✓ Cargados</span>
      ) : (
        <span className="text-2xs text-danger flex items-center gap-1">
          <AlertTriangle size={11} /> Faltan
        </span>
      ),
    width: "min",
  },
];

function ProductorDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const query = useQuery({
    queryKey: ["settlements", "productor", id],
    queryFn: () => settlementsApi.productorDetail(id),
  });

  const data = query.data?.data;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[620px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">{data?.nombre ?? `Productor #${id}`}</h2>
            {data?.email && (
              <a className="text-2xs text-accent hover:underline break-all" href={`mailto:${data.email}`}>
                {data.email}
              </a>
            )}
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {query.isLoading || !data ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando…
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <Section title="Datos bancarios">
                {data.paymentInfo ? (
                  <>
                    <Field label="Banco" value={data.paymentInfo.nombreBanco ?? "—"} />
                    <Field label="Tipo de cuenta" value={data.paymentInfo.tipoCuenta ?? "—"} />
                    <Field label="N° de cuenta" value={data.paymentInfo.numeroCuenta ?? "—"} />
                    <Field label="RUT" value={data.paymentInfo.rut ?? "—"} />
                    <Field
                      label="Email de pago"
                      value={
                        data.paymentInfo.emailPago ? (
                          <a className="text-accent hover:underline" href={`mailto:${data.paymentInfo.emailPago}`}>
                            {data.paymentInfo.emailPago}
                          </a>
                        ) : (
                          "—"
                        )
                      }
                      wide
                    />
                  </>
                ) : (
                  <div className="col-span-2 flex items-start gap-2 text-xs p-3 rounded-md border border-danger/40 bg-danger-soft text-fg">
                    <AlertTriangle size={14} className="text-danger shrink-0 mt-0.5" />
                    <span>
                      Este productor no tiene datos bancarios cargados. No podrá
                      recibir transferencias hasta completarlos desde su perfil.
                    </span>
                  </div>
                )}
              </Section>

              {data.clubs.map((club) => (
                <Section key={club.id} title={`${club.name} (${club.events.length} eventos)`}>
                  <div className="col-span-2 flex flex-col gap-2">
                    {club.events.length === 0 ? (
                      <p className="text-2xs text-fg-subtle">Sin eventos.</p>
                    ) : (
                      club.events.map((ev) => (
                        <div
                          key={ev.id}
                          className="border border-border-subtle rounded-md p-2.5 text-xs"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-medium text-fg truncate">{ev.nombre}</span>
                            <SettlementStatusPill status={ev.settlementStatus} />
                          </div>
                          <div className="text-2xs text-fg-subtle flex items-center gap-2 flex-wrap">
                            <span>{formatDateShort(ev.fecha)}</span>
                            <span>·</span>
                            <span>{ev.diasHabiles}d hábiles</span>
                            {ev.settlement && (
                              <>
                                <span>·</span>
                                <span className="text-fg-muted tabular-nums">
                                  {fmtCLP(Number(ev.settlement.amountToTransfer))}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Section>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function SettlementStatusPill({
  status,
}: {
  status: "sin_liquidar" | "generada" | "transferida";
}) {
  const map = {
    sin_liquidar: { cls: "nx-badge-neutral", label: "Sin liquidar" },
    generada: { cls: "nx-badge-warning", label: "Generada" },
    transferida: { cls: "nx-badge-success", label: "Transferida" },
  };
  const { cls, label } = map[status];
  return <span className={cls}>{label}</span>;
}
